import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { SubSink } from 'subsink';
import { EditReservationComponent } from '../cart/edit-reservation/edit-reservation.component';
import { EditReservation } from '../root-store/cart-store/actions';
import { SelectTreeItem } from '../root-store/object-tree-store/actions';
import { RootState } from '../root-store/root-state.model';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { ReservationDialogComponent, ReservationDialogData } from '../shared/reservation-dialog/reservation-dialog.component';
import { UserPasswordDialogData, UserPasswordFormMode } from '../user/user-password/user-password.component';
import { UserProfileDialogData, UserProfileFormMode } from '../user/user-profile/user-profile.component';
import { BackendService } from './backend.service';

export const panelClassMobileFullscreen = 'mobile-fullscreen'

@Injectable({
  providedIn: 'root'
})
export class DialogService implements OnDestroy {
  private subs = new SubSink()

  constructor(
    private dialog: MatDialog,
    private store: Store<RootState>,
    private backend: BackendService,
  ) { }

  async openObjectInfo(itemId: number, showBookingButton = false) {
    const [item, component] = await Promise.all([
      firstValueFrom(this.backend.getItemInfo(itemId)),
      import('../object-choice/object-info/object-info.component')
    ])

    const dialog = this.dialog.open(component.ObjectInfoComponent, {
      minWidth: '50vw',
      minHeight: '300px',
      maxWidth: '1000px',
      panelClass: panelClassMobileFullscreen,
      data: { id: itemId, item, showBookingButton },
      autoFocus: false,
      restoreFocus: false,
    })

    if (showBookingButton) {
      dialog.afterClosed().subscribe(res => {
        if (res?.bookNow)
          this.store.dispatch(SelectTreeItem({ itemId }))
      })
    }

    return dialog
  }

  async openEditProfile() {
    const component = (await import('../user/user-profile/user-profile.component')).UserProfileComponent
    return this.dialog.open(component, {
      width: '600px',
      data: { formMode: UserProfileFormMode.EditProfile } as UserProfileDialogData,
      autoFocus: false,
      panelClass: panelClassMobileFullscreen
    })
  }

  async openChangePassword() {
    const component = (await import('../user/user-password/user-password.component')).UserPasswordComponent
    return this.dialog.open(component, {
      data: { formMode: UserPasswordFormMode.Change } as UserPasswordDialogData,
      minWidth: 350
    })
  }

  async openEditReservation(): Promise<MatDialogRef<EditReservationComponent>> {
    const component = (await import('../cart/edit-reservation/edit-reservation.component')).EditReservationComponent
    const dialog = this.dialog.open(component, {
      width: '98vw',
      maxWidth: '98vw',
      height: 'calc(100vh - 80px)',
      panelClass: panelClassMobileFullscreen
    })

    // after the dialog is closed: unset the editingItem
    dialog.afterClosed().subscribe(() => {
      this.store.dispatch(EditReservation({ cartItem: null }))
    })

    return dialog
  }

  async openOrder(orderId: string, mode: ReservationDialogData['mode'] = 'cart'): Promise<MatDialogRef<ReservationDialogComponent>> {
    const component = await import('../shared/reservation-dialog/reservation-dialog.component').then(m => m.ReservationDialogComponent)
    return this.dialog.open(component, {
      width: '98vw',
      maxWidth: '98vw',
      height: 'calc(100vh - 80px)',
      panelClass: [panelClassMobileFullscreen],
      data: { orderId, mode } as ReservationDialogData,
      autoFocus: false
    })
  }

  // temporary disabled because of open-issue with material-dialogs and kendo recurrence-editor:
  // https://github.com/telerik/kendo-angular/issues/2883
  // openEditRecurrence(): void {
  //   this.lazyLoader.load('scheduler', null, 'recurrenceDialog').then(res => this.dialog.open(res.component, this.getDialogSize()))
  // }

  confirm(title: string, message: string, cancelText: string, confirmText: string): MatDialogRef<ConfirmDialogComponent, boolean> {
    return this.dialog.open(ConfirmDialogComponent, {
      data: { cancelText, confirmText, message, title },
      disableClose: true,
      autoFocus: false
    })
  }

  ngOnDestroy() {
    this.subs.unsubscribe()
  }
}
